import { Form9BulkRenewModal } from '../components/Form9BulkRenewModal';
import { useEffect, useState, useMemo } from 'react';
import { useSearchParams, Link } from 'react-router';
import { useAuth } from '../lib/auth.js';
import { api, ApiError } from '../lib/api.js';
import { Modal } from '../components/Modal';
import { useFormFields } from '../hooks/useFormFields.js';
import { Pagination } from '../components/Pagination';
import { AsyncSearchableSelect } from '../components/AsyncSearchableSelect.js';
import { fetchCompetentPersonOptions } from '../lib/api.js';
import { TableLoader } from '../components/TableLoader';
import { InlinePersonnelForm } from '../components/InlinePersonnelForm.js';
import { InlineTestMethodForm } from '../components/InlineTestMethodForm.js';
import { Form10BulkRenewModal } from '../components/Form10BulkRenewModal.js';
import { Form11BulkRenewModal } from '../components/Form11BulkRenewModal.js';
import { RenewalEmailConfigModal } from '../components/RenewalEmailConfigModal.js';
import { SendRenewalNoticeModal, RenewalNoticeGroup } from '../components/SendRenewalNoticeModal.js';
import { ShortCodeTag } from '../components/ShortCodeTag.js';
import { generateForm10ClientPDF } from '../lib/pdf-form10-client.js';
import { generateForm11ClientPDF } from '../lib/pdf-form11-client.js';
import { formatDateOnly } from '../lib/dateUtils';
import {
  DEFAULT_TEST_METHOD,
  buildTestMethodOptions,
  getEquipmentRule,
  buildPoint5Sentence,
} from '@gng-ehs/shared/equipment-rules';

export interface RenewalItem {
  id: string;
  certificate_number: string;
  company_name: string;
  company_id?: string;
  short_code?: string;
  equipment_name: string;
  internal_id_number: string;
  examination_date: string;
  next_inspection_date: string;
  test_method?: string;
  status: 'COMPLIANT' | 'EXPIRING_SOON' | 'OVERDUE';
  days_until_due: number;
  type?: 'FORM_9' | 'FORM_10' | 'FORM_11' | 'TRAINING';
  communication_email?: string;
  email_sent_count?: number;
}

export interface RenewalGroup {
  groupId: string;
  company_id: string;
  company_name: string;
  short_code?: string;
  examination_date: string;
  items: RenewalItem[];
  total_count: number;
  overdue_count: number;
  expiring_soon_count: number;
  compliant_count: number;
  earliest_due_date: string;
  communication_email?: string;
  email_sent_count: number;
  type: 'FORM_9' | 'FORM_10' | 'FORM_11' | 'TRAINING';
}

const STATUS_BADGE = {
  COMPLIANT: 'badge-green',
  EXPIRING_SOON: 'badge-orange',
  OVERDUE: 'badge-red',
} as const;

const STATUS_ORDER = ['OVERDUE', 'EXPIRING_SOON', 'COMPLIANT'];
const PAGE_SIZE = 20;
const CONDITION_OPTIONS = ['Good', 'Not Good', 'Not Applicable'];

/** Compute next inspection date for Form 10 (exam + 1 year - 1 day) */
function nextInspDate10(examISO: string): string {
  if (!examISO) return '';
  const d = new Date(examISO + 'T00:00:00');
  d.setFullYear(d.getFullYear() + 1);
  d.setDate(d.getDate() - 1);
  return d.toISOString().slice(0, 10);
}

/** Compute next inspection date for Form 11 (exam + 6 months - 1 day) */
function nextInspDate11(examISO: string): string {
  if (!examISO) return '';
  const d = new Date(examISO + 'T00:00:00');
  d.setMonth(d.getMonth() + 6);
  d.setDate(d.getDate() - 1);
  return d.toISOString().slice(0, 10);
}

/** Format ISO date to DD-MM-YYYY */
function toDDMMYYYY(iso: string): string {
  if (!iso) return '';
  try {
    const [y, m, d] = iso.split('-');
    return `${d?.padStart(2, '0')}-${m?.padStart(2, '0')}-${y}`;
  } catch {
    return iso;
  }
}

export function RenewalsPage() {
  const { canWrite } = useAuth();
  const isWrite = canWrite('renewals');

  const [searchParams] = useSearchParams();
  const initialStatus = searchParams.get('status') ?? 'ALL';

  const [items, setItems] = useState<RenewalItem[]>([]);
  const [summary, setSummary] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState(initialStatus);
  const [page, setPage] = useState(1);

  // Bulk Renewal modals state
  const [bulkForm9Ids, setBulkForm9Ids] = useState<string[]>([]);
  const [bulkForm10Open, setBulkForm10Open] = useState(false);
  const [bulkForm11Open, setBulkForm11Open] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // View mode: 'grouped' (Company + Inspection Date) or 'flat'
  const [viewMode, setViewMode] = useState<'grouped' | 'flat'>('grouped');
  const [collapsedGroupIds, setCollapsedGroupIds] = useState<Set<string>>(new Set());

  // Email Configuration & Manual Notice states
  const [emailConfigOpen, setEmailConfigOpen] = useState(false);
  const [sendNoticeOpen, setSendNoticeOpen] = useState(false);
  const [sendNoticeGroup, setSendNoticeGroup] = useState<RenewalNoticeGroup | null>(null);
  const [bulkSelectedItems, setBulkSelectedItems] = useState<RenewalItem[] | null>(null);

  // View Existing Certificate Modal State
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [viewCertItem, setViewCertItem] = useState<RenewalItem | null>(null);
  const [viewPdfBlobUrl, setViewPdfBlobUrl] = useState<string | null>(null);
  const [viewLoading, setViewLoading] = useState(false);
  const [viewError, setViewError] = useState('');

  // Renewal modal state
  const [renewOpen, setRenewOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<RenewalItem | null>(null);
  const [renewSaving, setRenewSaving] = useState(false);
  const [renewError, setRenewError] = useState('');

  // Full certificate data fetched when modal opens
  const [fullCertData, setFullCertData] = useState<Record<string, unknown> | null>(null);
  const [certLoading, setCertLoading] = useState(false);

  const fields = useFormFields({
    examination_date: '',
    personnel_id: '',
    test_method: DEFAULT_TEST_METHOD,
    // Form 10 fields
    defect_status: 'SAFE',
    defect_remarks: '',
    // Form 11 fittings fields (Section 5: Points 10 to 16)
    condition_vessel: 'Good',
    condition_piping: 'Good',
    fittings_pressure_gauges: 'Good',
    fittings_safety_valve: 'Good',
    fittings_stop_valve: 'Good',
    fittings_reducing_valve: 'Not Applicable',
    fittings_additional_safety_valve: 'Not Applicable',
    fittings_other_devices: 'Not Applicable',
    repairs_required: 'NIL',
    repairs_period: 'Not Applicable',
    repairs_other_condition: 'NIL',
    reduced_working_pressure_pending_repairs: 'Not Required',
    safe_working_pressure_subrule_8: 'Not Applicable',
    other_observations: 'Safety valve operation and setting to be checked regularly.',
  });

  // Inline personnel creation state
  const [showPersonnelForm, setShowPersonnelForm] = useState(false);
  const [customTestMethods, setCustomTestMethods] = useState<Array<{ id: string; name: string }>>([]);
  const [showInlineTestMethodForm, setShowInlineTestMethodForm] = useState(false);

  useEffect(() => {
    api.testMethods.list()
      .then((res) => {
        if (res.data) setCustomTestMethods(res.data);
      })
      .catch(() => {});
  }, []);

  const allTestMethodOptions = useMemo(() => {
    return buildTestMethodOptions(customTestMethods);
  }, [customTestMethods]);

  const load = () => {
    setLoading(true);
    api.dashboard.renewals({ status: statusFilter === 'ALL' ? undefined : statusFilter })
      .then((res: any) => {
        const rawItems = Array.isArray(res.data) ? res.data : res.data?.data ?? [];
        const rawSummary = res.summary ?? res.data?.summary ?? {};
        setItems(rawItems as RenewalItem[]);
        setSummary(rawSummary);
      })
      .catch((e: ApiError) => setError(e.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [statusFilter]);
  useEffect(() => { setPage(1); }, [statusFilter]);

  // Groups calculated strictly by: Company + Physical Inspection Date + Certificate Type
  const groups = useMemo<RenewalGroup[]>(() => {
    const map = new Map<string, RenewalGroup>();

    for (const item of items) {
      const examDate = item.examination_date || 'Unknown';
      const certType = item.type || 'FORM_10';
      const key = `${item.company_id || item.company_name}::${examDate}::${certType}`;

      if (!map.has(key)) {
        map.set(key, {
          groupId: key,
          company_id: item.company_id || '',
          company_name: item.company_name,
          short_code: item.short_code,
          examination_date: examDate,
          type: certType,
          items: [],
          total_count: 0,
          overdue_count: 0,
          expiring_soon_count: 0,
          compliant_count: 0,
          earliest_due_date: item.next_inspection_date,
          communication_email: item.communication_email,
          email_sent_count: 0,
        });
      }

      const g = map.get(key)!;
      g.items.push(item);
      g.total_count += 1;
      if (item.status === 'OVERDUE') g.overdue_count += 1;
      else if (item.status === 'EXPIRING_SOON') g.expiring_soon_count += 1;
      else if (item.status === 'COMPLIANT') g.compliant_count += 1;

      if (!g.earliest_due_date || (item.next_inspection_date && item.next_inspection_date < g.earliest_due_date)) {
        g.earliest_due_date = item.next_inspection_date;
      }

      if (!g.communication_email && item.communication_email) {
        g.communication_email = item.communication_email;
      }
      if (!g.short_code && item.short_code) {
        g.short_code = item.short_code;
      }

      g.email_sent_count = Math.max(g.email_sent_count, item.email_sent_count || 0);
    }

    // Sort groups: overdue groups first, then expiring soon, then by earliest due date
    return Array.from(map.values()).sort((a, b) => {
      if (a.overdue_count > 0 && b.overdue_count === 0) return -1;
      if (b.overdue_count > 0 && a.overdue_count === 0) return 1;
      if (a.expiring_soon_count > 0 && b.expiring_soon_count === 0) return -1;
      if (b.expiring_soon_count > 0 && a.expiring_soon_count === 0) return 1;
      return (a.earliest_due_date || '').localeCompare(b.earliest_due_date || '');
    });
  }, [items]);

  const GROUPS_PAGE_SIZE = 10;
  const totalGroupPages = Math.max(1, Math.ceil(groups.length / GROUPS_PAGE_SIZE));
  const pagedGroups = useMemo(
    () => groups.slice((page - 1) * GROUPS_PAGE_SIZE, page * GROUPS_PAGE_SIZE),
    [groups, page]
  );

  // Client-side pagination (all data is already loaded)
  const totalPages = Math.max(1, Math.ceil(items.length / PAGE_SIZE));
  const pagedItems = useMemo(
    () => items.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [items, page],
  );

  const toggleGroupCollapse = (groupId: string) => {
    setCollapsedGroupIds((prev) => {
      const next = new Set(prev);
      if (next.has(groupId)) next.delete(groupId);
      else next.add(groupId);
      return next;
    });
  };

  const handleOpenGroupNotice = (group: RenewalGroup) => {
    const noticeGroup: RenewalNoticeGroup = {
      groupId: group.groupId,
      company_id: group.company_id,
      company_name: group.company_name,
      short_code: group.short_code,
      examination_date: group.examination_date,
      type: group.type,
      items: group.items.map((i) => ({
        id: i.id,
        certificate_number: i.certificate_number,
        equipment_name: i.equipment_name,
        internal_id_number: i.internal_id_number,
        examination_date: i.examination_date,
        next_inspection_date: i.next_inspection_date,
        days_until_due: i.days_until_due,
        status: i.status,
        type: i.type,
        communication_email: i.communication_email,
        email_sent_count: i.email_sent_count,
      })),
      total_count: group.total_count,
      overdue_count: group.overdue_count,
      expiring_soon_count: group.expiring_soon_count,
      compliant_count: group.compliant_count,
      earliest_due_date: group.earliest_due_date,
      communication_email: group.communication_email,
      email_sent_count: group.email_sent_count,
    };
    setSendNoticeGroup(noticeGroup);
    setSendNoticeOpen(true);
  };

  const handleRenewGroup = (group: RenewalGroup) => {
    if (group.type === 'FORM_9') {
      setBulkForm9Ids(group.items.map(i => i.id));
    } else if (group.type === 'FORM_10') {
      setBulkSelectedItems(group.items);
      setBulkForm10Open(true);
    } else if (group.type === 'FORM_11') {
      setBulkSelectedItems(group.items);
      setBulkForm11Open(true);
    }
  };

  const handleToggleSelectGroup = (group: RenewalGroup) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      const allInGroupSelected = group.items.every((i) => next.has(i.id));
      if (allInGroupSelected) {
        group.items.forEach((i) => next.delete(i.id));
      } else {
        group.items.forEach((i) => next.add(i.id));
      }
      return next;
    });
  };

  const handleToggleSelectAll = () => {
    if (selectedIds.size === pagedItems.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(pagedItems.map((i) => i.id)));
    }
  };

  const handleToggleSelectItem = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectedItemsList = useMemo(() => {
    return items.filter((i) => selectedIds.has(i.id));
  }, [items, selectedIds]);

  const openViewCert = async (item: RenewalItem) => {
    setViewCertItem(item);
    setViewModalOpen(true);
    setViewLoading(true);
    setViewError('');
    if (viewPdfBlobUrl) {
      URL.revokeObjectURL(viewPdfBlobUrl);
      setViewPdfBlobUrl(null);
    }
    try {
      let url = '';
      if (item.type === 'FORM_11') {
        url = await api.form11.getPdfBlobUrl(item.id);
      } else if (item.type === 'TRAINING') {
        url = await api.training.getPdfBlobUrl(item.id);
      } else {
        url = item.type === 'FORM_9' ? await api.form9.getPdfBlobUrl(item.id) : await api.form10.getPdfBlobUrl(item.id);
      }
      setViewPdfBlobUrl(url);
    } catch (err: unknown) {
      setViewError(err instanceof Error ? err.message : 'Failed to fetch certificate PDF');
    } finally {
      setViewLoading(false);
    }
  };

  const closeViewModal = () => {
    setViewModalOpen(false);
    if (viewPdfBlobUrl) {
      URL.revokeObjectURL(viewPdfBlobUrl);
      setViewPdfBlobUrl(null);
    }
    setViewCertItem(null);
  };

  const handleDownloadViewCert = async () => {
    if (!viewCertItem) return;
    try {
      if (viewCertItem.type === 'FORM_11') {
        await api.form11.downloadPdf(viewCertItem.id);
      } else if (viewCertItem.type === 'TRAINING') {
        await api.training.downloadPdf(viewCertItem.id);
      } else {
        await (viewCertItem.type === 'FORM_9' ? api.form9 : api.form10).downloadPdf(viewCertItem.id);
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to download certificate PDF');
    }
  };

  const openRenew = async (item: RenewalItem) => {
    if (item.type === 'FORM_9') { window.location.assign(`/form9?renew=${encodeURIComponent(item.id)}`); return; }
    setSelectedItem(item);
    setRenewError('');
    setFullCertData(null);
    setCertLoading(true);
    setRenewOpen(true);
    setShowPersonnelForm(false);
    setShowInlineTestMethodForm(false);

    // Set basic fields
    fields.setField('examination_date', new Date().toISOString().slice(0, 10));
    fields.setField('test_method', item.test_method || DEFAULT_TEST_METHOD);
    fields.setField('personnel_id', '');

    try {
      // Fetch full certificate data
      const res = item.type === 'FORM_11'
        ? await api.form11.get(item.id)
        : await api.form10.get(item.id);
      const cert = (res as any).data ?? res;
      setFullCertData(cert as Record<string, unknown>);

      // Pre-fill form-specific fields from old cert
      if (item.type === 'FORM_11') {
        fields.patchValues({
          condition_vessel: (cert.condition_vessel as string) || 'Good',
          condition_piping: (cert.condition_piping as string) || 'Good',
          fittings_pressure_gauges: (cert.fittings_pressure_gauges as string) || 'Good',
          fittings_safety_valve: (cert.fittings_safety_valve as string) || 'Good',
          fittings_stop_valve: (cert.fittings_stop_valve as string) || 'Good',
          fittings_reducing_valve: (cert.fittings_reducing_valve as string) || 'Not Applicable',
          fittings_additional_safety_valve: (cert.fittings_additional_safety_valve as string) || 'Not Applicable',
          fittings_other_devices: (cert.fittings_other_devices as string) || 'Not Applicable',
          repairs_required: (cert.repairs_required as string) ?? 'NIL',
          repairs_period: (cert.repairs_period as string) ?? 'Not Applicable',
          repairs_other_condition: (cert.repairs_other_condition as string) ?? 'NIL',
          reduced_working_pressure_pending_repairs: (cert.reduced_working_pressure_pending_repairs as string) ?? 'Not Required',
          safe_working_pressure_subrule_8: (cert.safe_working_pressure_subrule_8 as string) ?? 'Not Applicable',
          other_observations: (cert.other_observations as string) ?? 'Safety valve operation and setting to be checked regularly.',
        });
      } else {
        fields.patchValues({
          defect_status: (cert.defect_status as string) || 'SAFE',
          defect_remarks: (cert.defect_remarks as string) || '',
          test_method: (cert.test_method as string) || DEFAULT_TEST_METHOD,
        });
      }
    } catch {
      // If fetch fails, proceed with defaults
    } finally {
      setCertLoading(false);
    }
  };

  // Live PDF preview — computed reactively from form fields + full cert data
  const livePdfBlobUrl = useMemo(() => {
    if (!selectedItem || !fullCertData) return null;
    try {
      if (selectedItem.type === 'FORM_11') {
        const examISO = fields.values.examination_date;
        const nextInsp = nextInspDate11(examISO);
        const examDateDD = toDDMMYYYY(examISO);
        const cert = fullCertData;

        const extFindings = `(a) Good on ${examDateDD}`;

        return generateForm11ClientPDF({
          certificate_number: `${selectedItem.certificate_number} (Renewed)`,
          examination_date: examISO,
          next_inspection_date: nextInsp,
          company: {
            company_name: cert.company_name as string,
            occupier_name: cert.occupier_name as string,
            factory_address: cert.factory_address as string,
            license_number: cert.license_number as string,
            registration_number: cert.registration_number as string,
            nic_code: cert.nic_code as string,
          },
          vessel_name_description: cert.vessel_name_description as string,
          vessel_serial_no: cert.vessel_serial_no as string,
          vessel_capacity: cert.vessel_capacity as string,
          vessel_model_no: cert.vessel_model_no as string,
          vessel_location: cert.vessel_location as string,
          manufacturer_name_address: cert.manufacturer_name_address as string,
          nature_of_process: cert.nature_of_process as string,
          date_of_construction: cert.date_of_construction as string,
          thickness_shell: cert.thickness_shell as string,
          thickness_dish: cert.thickness_dish as string,
          thickness_top_dish: cert.thickness_top_dish as string,
          thickness_bottom_dish: cert.thickness_bottom_dish as string,
          first_taken_into_use_date: cert.first_taken_into_use_date as string,
          safe_working_pressure_recommended: cert.safe_working_pressure_recommended as string,
          last_external_exam_date: toDDMMYYYY(cert.examination_date as string),
          last_internal_exam_date: cert.last_internal_exam_date as string,
          last_hydraulic_exam_details: cert.last_hydraulic_exam_details as string,
          last_ultrasonic_exam_date: cert.last_ultrasonic_exam_date as string,
          lagging_removed_status: cert.lagging_removed_status as string,
          exam_external_findings: extFindings,
          exam_internal_findings: cert.exam_internal_findings as string,
          exam_hydraulic_findings: cert.exam_hydraulic_findings as string,
          exam_ultrasonic_findings: cert.exam_ultrasonic_findings as string,
          condition_vessel: fields.values.condition_vessel,
          condition_piping: fields.values.condition_piping,
          fittings_pressure_gauges: fields.values.fittings_pressure_gauges,
          fittings_safety_valve: fields.values.fittings_safety_valve,
          fittings_stop_valve: fields.values.fittings_stop_valve,
          fittings_reducing_valve: fields.values.fittings_reducing_valve,
          fittings_additional_safety_valve: fields.values.fittings_additional_safety_valve,
          fittings_other_devices: fields.values.fittings_other_devices,
          safe_working_pressure_recommended_after: cert.safe_working_pressure_recommended_after as string,
          repairs_required: fields.values.repairs_required,
          repairs_period: fields.values.repairs_period,
          repairs_other_condition: fields.values.repairs_other_condition,
          reduced_working_pressure_pending_repairs: fields.values.reduced_working_pressure_pending_repairs,
          safe_working_pressure_subrule_8: fields.values.safe_working_pressure_subrule_8,
          other_observations: fields.values.other_observations,
        });
      } else {
        // Form 10 live preview
        const cert = fullCertData;
        const examISO = fields.values.examination_date;
        const nextInsp = nextInspDate10(examISO);
        const examDateDD = toDDMMYYYY(examISO);

        const equipName = (cert.equipment_short_code as string) || (cert.equipment_name_type as string) || '';
        const rule = equipName ? getEquipmentRule(equipName) : undefined;
        const swl = (cert.swl_value as number) ?? null;
        const testMethod = fields.values.test_method || DEFAULT_TEST_METHOD;
        const point5 = buildPoint5Sentence(rule ?? undefined, swl, examDateDD, (cert.inspector_name as string) || 'Competent Person', testMethod);

        return generateForm10ClientPDF({
          certificate_number: `${selectedItem.certificate_number} (Renewed)`,
          examination_date: examISO,
          company: {
            license_number: cert.license_number as string,
            registration_number: cert.registration_number as string,
            nic_code: cert.nic_code as string,
            occupier_name: cert.occupier_name as string,
            company_name: cert.company_name as string,
            factory_address: cert.factory_address as string,
          },
          equipment: {
            equipment_short_code: cert.equipment_short_code as string,
            internal_id_number: cert.internal_id_number as string,
            swl_value: cert.swl_value as number,
            swl_text: cert.swl_text as string,
            lift_height: cert.lift_height as string,
            make: cert.make as string,
            model: cert.model as string,
            equipment_size: cert.equipment_size as string,
            location_in_factory: cert.location_in_factory as string,
            first_use_value: cert.first_use_value as string,
          },
          test_prefix_sentence: point5,
          previous_inspection_details: `Last Inspection done on ${toDDMMYYYY(cert.examination_date as string)} by ${(cert.inspector_name as string) || 'Competent Person'}`,
          annealing_details: cert.annealing_details as string,
          defect_status: fields.values.defect_status,
          defect_remarks: (fields.values.defect_status === 'SAFE' || fields.values.defect_status === 'NOT_APPLICABLE') ? undefined : fields.values.defect_remarks,
          next_inspection_date: nextInsp,
        });
      }
    } catch {
      return null;
    }
  }, [fields.values, selectedItem, fullCertData]);

  const handleRenew = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedItem) return;
    setRenewSaving(true);
    setRenewError('');
    try {
      const payload: Record<string, unknown> = {
        old_certificate_id: selectedItem.id,
        examination_date: fields.values.examination_date,
        personnel_id: fields.values.personnel_id,
      };

      if (selectedItem.type === 'FORM_11') {
        // Form 11 specific — fittings & remarks overrides (Points 10 to 16)
        payload.condition_vessel = fields.values.condition_vessel;
        payload.condition_piping = fields.values.condition_piping;
        payload.fittings_pressure_gauges = fields.values.fittings_pressure_gauges;
        payload.fittings_safety_valve = fields.values.fittings_safety_valve;
        payload.fittings_stop_valve = fields.values.fittings_stop_valve;
        payload.fittings_reducing_valve = fields.values.fittings_reducing_valve;
        payload.fittings_additional_safety_valve = fields.values.fittings_additional_safety_valve;
        payload.fittings_other_devices = fields.values.fittings_other_devices;
        payload.repairs_required = fields.values.repairs_required;
        payload.repairs_period = fields.values.repairs_period;
        payload.repairs_other_condition = fields.values.repairs_other_condition;
        payload.reduced_working_pressure_pending_repairs = fields.values.reduced_working_pressure_pending_repairs;
        payload.safe_working_pressure_subrule_8 = fields.values.safe_working_pressure_subrule_8;
        payload.other_observations = fields.values.other_observations;
      } else {
        // Form 10 specific — defect status + test method
        payload.test_method = fields.values.test_method || DEFAULT_TEST_METHOD;
        payload.defect_status = fields.values.defect_status;
        payload.defect_remarks = (fields.values.defect_status === 'SAFE' || fields.values.defect_status === 'NOT_APPLICABLE')
          ? '' : fields.values.defect_remarks;
      }

      await api.dashboard.renew(payload);
      setRenewOpen(false);
      load();
    } catch (err: unknown) {
      setRenewError(err instanceof Error ? err.message : 'Renewal failed');
    } finally {
      setRenewSaving(false);
    }
  };

  return (
    <div className="flex-1 flex flex-col min-h-0 space-y-4 overflow-hidden pt-1 px-1">
      {/* Summary cards */}
      <div className="shrink-0 grid grid-cols-1 sm:grid-cols-3 gap-4">
        {STATUS_ORDER.map((s) => (
          <div key={s} className={`card p-4 border-l-4 ${s === 'OVERDUE' ? 'border-l-red-500' : s === 'EXPIRING_SOON' ? 'border-l-orange-500' : 'border-l-green-500'}`}>
            <p className="text-sm text-gray-500">{s.replace('_', ' ')}</p>
            <p className={`text-2xl font-bold mt-1 ${s === 'OVERDUE' ? 'text-red-600' : s === 'EXPIRING_SOON' ? 'text-orange-600' : 'text-green-600'}`}>
              {summary[s.toLowerCase()] ?? 0}
            </p>
          </div>
        ))}
      </div>

      {/* Filter & Action Toolbar */}
      <div className="shrink-0 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-3">
          {/* Status Filters */}
          <div className="flex flex-wrap items-center gap-1.5 bg-slate-100 p-1 rounded-lg border border-slate-200">
            {['ALL', 'OVERDUE', 'EXPIRING_SOON', 'COMPLIANT'].map((s) => (
              <button
                key={s}
                onClick={() => { setStatusFilter(s); setSelectedIds(new Set()); setPage(1); }}
                className={`btn text-xs py-1 px-2.5 rounded-md transition-all cursor-pointer ${
                  statusFilter === s ? 'btn-primary shadow-xs' : 'bg-transparent hover:bg-slate-200 text-slate-700'
                }`}
              >
                {s}
              </button>
            ))}
          </div>

          {/* View Mode Switcher: Grouped Batches vs Flat List */}
          <div className="flex items-center bg-slate-100 p-1 rounded-lg border border-slate-200">
            <button
              type="button"
              onClick={() => { setViewMode('grouped'); setPage(1); }}
              className={`px-3 py-1 text-xs font-semibold rounded-md transition-all cursor-pointer ${
                viewMode === 'grouped'
                  ? 'bg-white text-blue-800 shadow-xs'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              🗂️ Grouped Batches ({groups.length})
            </button>
            <button
              type="button"
              onClick={() => { setViewMode('flat'); setPage(1); }}
              className={`px-3 py-1 text-xs font-semibold rounded-md transition-all cursor-pointer ${
                viewMode === 'flat'
                  ? 'bg-white text-blue-800 shadow-xs'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              📋 Flat List ({items.length})
            </button>
          </div>

          {/* Refresh Button */}
          <button
            type="button"
            onClick={load}
            disabled={loading}
            title="Refresh renewal records"
            className="btn btn-secondary text-xs py-1.5 px-3 rounded-lg inline-flex items-center gap-1.5 cursor-pointer shadow-2xs hover:bg-slate-100"
          >
            <svg
              className={`w-3.5 h-3.5 text-slate-500 ${loading ? 'animate-spin text-brand-600' : ''}`}
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <span>Refresh</span>
          </button>
        </div>

        <div className="flex flex-wrap items-center gap-2 justify-end shrink-0">
          {isWrite && (
            <>
              <button
                type="button"
                onClick={() => setEmailConfigOpen(true)}
                className="btn text-xs py-1.5 bg-slate-800 hover:bg-slate-900 text-white font-semibold shadow-sm inline-flex items-center gap-1.5 cursor-pointer"
                title="Configure renewal email timing rules and dynamic templates"
              >
                <span>⚙️</span>
                <span>Renewal Email Configuration</span>
              </button>
              <button
                type="button"
                onClick={() => {
                  setBulkSelectedItems(null);
                  setBulkForm10Open(true);
                }}
                className="btn text-xs py-1.5 bg-blue-600 hover:bg-blue-700 text-white font-semibold shadow-sm inline-flex items-center gap-1.5 cursor-pointer"
              >
                <span>⚡</span>
                <span>Bulk Renew Form 10</span>
              </button>
              <button
                type="button"
                onClick={() => {
                  setBulkSelectedItems(null);
                  setBulkForm11Open(true);
                }}
                className="btn text-xs py-1.5 bg-purple-700 hover:bg-purple-800 text-white font-semibold shadow-sm inline-flex items-center gap-1.5 cursor-pointer"
              >
                <span>⚡</span>
                <span>Bulk Renew Form 11</span>
              </button>
            </>
          )}
        </div>
      </div>

      {error && <ErrorBanner message={error} onDismiss={() => setError('')} />}

      {loading ? (
        <TableLoader />
      ) : viewMode === 'grouped' ? (
        /* Grouped Batches View */
        <div className="flex-1 min-h-0 flex flex-col overflow-hidden space-y-3">
          <div className="flex-1 min-h-0 overflow-y-auto space-y-3.5 pr-1">
            {pagedGroups.length === 0 ? (
              <div className="card p-12 text-center text-slate-500 font-medium">
                No certificate renewal batches found.
              </div>
            ) : (
              pagedGroups.map((group) => (
                <RenewalGroupCard
                  key={group.groupId}
                  group={group}
                  isWrite={isWrite}
                  isCollapsed={collapsedGroupIds.has(group.groupId)}
                  onToggleCollapse={() => toggleGroupCollapse(group.groupId)}
                  onSendGroupNotice={() => handleOpenGroupNotice(group)}
                  onRenewGroup={() => handleRenewGroup(group)}
                  onOpenViewCert={openViewCert}
                  onOpenRenewItem={openRenew}
                  selectedIds={selectedIds}
                  onToggleSelectItem={handleToggleSelectItem}
                  onToggleSelectGroup={() => handleToggleSelectGroup(group)}
                />
              ))
            )}
          </div>
          <div className="shrink-0 pt-1">
            <Pagination page={page} totalPages={totalGroupPages} total={groups.length} onPageChange={setPage} />
          </div>
        </div>
      ) : (
        /* Flat List View */
        <div className="flex-1 min-h-0 flex flex-col overflow-hidden space-y-2">
          <div className="table-wrapper">
            <table className="table">
              <thead className="sticky top-0 z-10 bg-slate-50">
                <tr>
                  {isWrite && (
                    <th className="w-10 text-center">
                      <input
                        type="checkbox"
                        checked={pagedItems.length > 0 && pagedItems.every((i) => selectedIds.has(i.id))}
                        onChange={handleToggleSelectAll}
                        className="rounded border-slate-300 text-brand-600 focus:ring-brand-500 cursor-pointer"
                        title="Select/Deselect All on Page"
                      />
                    </th>
                  )}
                  <th>REPORT / CERT NO.</th>
                  <th>Company</th>
                  <th>Equipment / Vessel</th>
                  <th>Last Exam</th>
                  <th>Next Due</th>
                  <th>Days</th>
                  <th>Status</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {pagedItems.length === 0 ? (
                  <tr><td colSpan={isWrite ? 9 : 8} className="text-center text-gray-500 py-8">No certificates found</td></tr>
                ) : pagedItems.map((item) => (
                  <tr key={item.id} className={item.status === 'OVERDUE' ? 'bg-red-50/50' : item.status === 'EXPIRING_SOON' ? 'bg-orange-50/50' : ''}>
                    {isWrite && (
                      <td className="text-center">
                        <input
                          type="checkbox"
                          checked={selectedIds.has(item.id)}
                          onChange={() => handleToggleSelectItem(item.id)}
                          className="rounded border-slate-300 text-brand-600 focus:ring-brand-500 cursor-pointer"
                        />
                      </td>
                    )}
                    <td>
                      <button
                        type="button"
                        onClick={() => openViewCert(item)}
                        className="font-mono text-xs font-bold text-brand-700 hover:text-brand-900 hover:underline cursor-pointer text-left block"
                        title="Click to view certificate PDF"
                      >
                        {item.certificate_number}
                      </button>
                      <span className={`inline-block mt-0.5 px-1.5 py-0.5 rounded text-[10px] font-bold ${
                        item.type === 'FORM_11'
                          ? 'bg-purple-50 text-purple-700 border border-purple-200'
                          : item.type === 'TRAINING'
                          ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                          : 'bg-blue-50 text-blue-700 border border-blue-200'
                      }`}>
                        {item.type === 'FORM_9' ? 'Form 9' : item.type === 'FORM_11' ? 'Form 11' : item.type === 'TRAINING' ? 'Training Cert' : 'Form 10'}
                      </span>
                    </td>
                    <td className="font-medium text-sm text-gray-900">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span>{item.company_name}</span>
                        {item.short_code && <ShortCodeTag code={item.short_code} />}
                      </div>
                    </td>
                    <td className="text-xs">
                      <span className="font-medium text-gray-900">{item.equipment_name}</span>
                      {item.internal_id_number && item.internal_id_number !== '—' && (
                        <span className="block text-[11px] font-mono text-gray-500 mt-0.5">
                          Tag: {item.internal_id_number}
                        </span>
                      )}
                    </td>
                    <td className="text-sm text-gray-500">{formatDateOnly(item.examination_date)}</td>
                    <td className="text-sm font-medium">{formatDateOnly(item.next_inspection_date)}</td>
                    <td className="text-sm">
                      <span className={item.days_until_due < 0 ? 'text-red-600 font-bold' : item.days_until_due <= 30 ? 'text-orange-600 font-bold' : 'text-gray-500'}>
                        {item.days_until_due > 0 ? `${item.days_until_due}d` : `${item.days_until_due}d overdue`}
                      </span>
                    </td>
                    <td><span className={`badge ${STATUS_BADGE[item.status]}`}>{item.status}</span></td>
                    {isWrite ? (
                      <td>
                        <div className="flex items-center gap-1.5">
                          {item.type === 'TRAINING' ? (
                            <Link
                              to="/training"
                              title="Go to Training Certificates"
                              className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold bg-emerald-50 text-emerald-700 hover:bg-emerald-100 border border-emerald-200/80 transition-colors"
                            >
                              <span>Manage</span>
                            </Link>
                          ) : (
                            <button
                              type="button"
                              onClick={() => openRenew(item)}
                              title="Renew Certificate"
                              className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold bg-brand-50 text-brand-700 hover:bg-brand-100 border border-brand-200/80 transition-colors cursor-pointer"
                            >
                              <svg className="w-3.5 h-3.5 text-brand-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                              <span>Renew</span>
                            </button>
                          )}
                        </div>
                      </td>
                    ) : (
                      <td className="text-xs text-gray-400">—</td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="shrink-0 pt-1">
            <Pagination page={page} totalPages={totalPages} total={items.length} onPageChange={setPage} />
          </div>
        </div>
      )}

      {/* Renewal Modal — Full Page Split View */}
      <Modal open={renewOpen} onClose={() => setRenewOpen(false)} title="Renew Certificate" size="full">
        {selectedItem && (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 h-[calc(100vh-65px)] items-stretch">
            {/* Left Panel: Form */}
            <div className="lg:col-span-5 space-y-4 overflow-y-auto h-full pr-3 pb-6">
              <form onSubmit={handleRenew} className="space-y-4">
                {/* Info Card */}
                <div className="rounded-xl bg-slate-50 border border-slate-200/80 p-4">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                    <div>
                      <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Current Certificate</span>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="font-mono text-base font-bold text-slate-900">{selectedItem.certificate_number}</span>
                        <span className={`inline-block px-2 py-0.5 rounded text-[11px] font-bold ${selectedItem.type === 'FORM_11' ? 'bg-purple-100 text-purple-800 border border-purple-200' : 'bg-blue-100 text-blue-800 border border-blue-200'}`}>
                          {selectedItem.type === 'FORM_9' ? 'Form 9' : selectedItem.type === 'FORM_11' ? 'Form 11' : 'Form 10'}
                        </span>
                      </div>
                    </div>
                    <div className="text-left sm:text-right">
                      <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Company & Asset</span>
                      <p className="text-sm font-semibold text-slate-800 mt-0.5">{selectedItem.company_name}</p>
                      <p className="text-xs text-slate-500">{selectedItem.equipment_name} {selectedItem.internal_id_number && selectedItem.internal_id_number !== '—' ? `(${selectedItem.internal_id_number})` : ''}</p>
                    </div>
                  </div>
                </div>

                {certLoading ? (
                  <div className="py-8 flex flex-col items-center justify-center space-y-2">
                    <div className="animate-spin rounded-full h-7 w-7 border-b-2 border-brand-600" />
                    <p className="text-xs text-gray-500">Loading certificate data…</p>
                  </div>
                ) : (
                  <>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <Field label="New Examination Date" required>
                        <input className="input h-10 text-sm" type="date" {...fields.bind('examination_date')} required />
                      </Field>
                      <Field
                        label="Competent Person"
                        required
                        hint={
                          <button
                            type="button"
                            onClick={() => setShowPersonnelForm((v) => !v)}
                            className="text-xs text-brand-600 hover:text-brand-700 font-medium inline-flex items-center gap-1 cursor-pointer"
                          >
                            <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M12 5v14M5 12h14" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                            {showPersonnelForm ? 'Choose existing' : 'New Personnel'}
                          </button>
                        }
                      >
                        <AsyncSearchableSelect
                          value={fields.values.personnel_id}
                          onChange={(v) => fields.setField('personnel_id', v)}
                          fetchOptions={fetchCompetentPersonOptions}
                          placeholder="Search inspector…"
                          disabled={showPersonnelForm}
                        />
                      </Field>
                    </div>

                    {/* Full Inline Personnel Creation Form */}
                    {showPersonnelForm && (
                      <InlinePersonnelForm
                        initialRole="competent_person"
                        onCreated={(created) => {
                          fields.setField('personnel_id', created.id);
                          setShowPersonnelForm(false);
                        }}
                        onCancel={() => setShowPersonnelForm(false)}
                      />
                    )}

                    {/* ===== Form 10 Specific Fields ===== */}
                    {selectedItem.type !== 'FORM_11' && (
                      <>
                        {/* Test Method */}
                        <div className="space-y-2">
                          <Field
                            label="Test Method (Point 5)"
                            required
                            hint={
                              <button
                                type="button"
                                onClick={() => setShowInlineTestMethodForm((v) => !v)}
                                className="text-xs text-brand-600 hover:text-brand-700 font-medium inline-flex items-center gap-1 cursor-pointer"
                              >
                                <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <path d="M12 5v14M5 12h14" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                                {showInlineTestMethodForm ? 'Hide Form ✕' : 'New Method'}
                              </button>
                            }
                          >
                            <select className="input h-10 text-sm" {...fields.bind('test_method')}>
                              {allTestMethodOptions.map((opt) => (
                                <option key={opt.value} value={opt.value}>
                                  {opt.label}
                                </option>
                              ))}
                            </select>
                          </Field>
                          {showInlineTestMethodForm && (
                            <InlineTestMethodForm
                              onCreated={(created) => {
                                setCustomTestMethods((prev) => [...prev, created]);
                                fields.setField('test_method', created.name);
                                setShowInlineTestMethodForm(false);
                              }}
                              onCancel={() => setShowInlineTestMethodForm(false)}
                            />
                          )}
                        </div>

                        {/* Defect Status */}
                        <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 space-y-3">
                          <h3 className="text-xs font-semibold text-slate-700 uppercase tracking-wider flex items-center gap-1.5">
                            <svg className="w-4 h-4 text-slate-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                            Inspection Result
                          </h3>
                          <div className="grid grid-cols-1 gap-3">
                            <Field label="Defect Status" required>
                              <select className="input h-10 text-sm" {...fields.bind('defect_status')}>
                                <option value="SAFE">Safe</option>
                                <option value="REJECT">Reject</option>
                                <option value="NOT_APPLICABLE">Not Applicable</option>
                              </select>
                            </Field>
                            {fields.values.defect_status !== 'SAFE' && fields.values.defect_status !== 'NOT_APPLICABLE' && (
                              <Field label="Defect Remarks (Point 8)">
                                <textarea
                                  className="input text-sm"
                                  rows={2}
                                  {...fields.bind('defect_remarks')}
                                  placeholder="Describe reason for reject / defect & remedial steps"
                                />
                              </Field>
                            )}
                          </div>
                        </div>
                      </>
                    )}

                    {/* ===== Form 11 Specific Fields: Section 5 (Points 10 to 16) ===== */}
                    {selectedItem.type === 'FORM_11' && (
                      <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 space-y-4">
                        <h3 className="text-sm font-semibold text-slate-800 uppercase tracking-wider">
                          5. Fittings Condition & Remarks (Points 10 to 16)
                        </h3>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Vessel Condition</label>
                            <select
                              value={fields.values.condition_vessel}
                              onChange={(e) => fields.setField('condition_vessel', e.target.value)}
                              className="w-full px-2.5 py-1.5 border border-gray-300 rounded text-xs bg-white"
                            >
                              {CONDITION_OPTIONS.map((c) => <option key={c} value={c}>{c}</option>)}
                            </select>
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Piping Condition</label>
                            <select
                              value={fields.values.condition_piping}
                              onChange={(e) => fields.setField('condition_piping', e.target.value)}
                              className="w-full px-2.5 py-1.5 border border-gray-300 rounded text-xs bg-white"
                            >
                              {CONDITION_OPTIONS.map((c) => <option key={c} value={c}>{c}</option>)}
                            </select>
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Pressure Gauges</label>
                            <select
                              value={fields.values.fittings_pressure_gauges}
                              onChange={(e) => fields.setField('fittings_pressure_gauges', e.target.value)}
                              className="w-full px-2.5 py-1.5 border border-gray-300 rounded text-xs bg-white"
                            >
                              {CONDITION_OPTIONS.map((c) => <option key={c} value={c}>{c}</option>)}
                            </select>
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Safety Valve</label>
                            <select
                              value={fields.values.fittings_safety_valve}
                              onChange={(e) => fields.setField('fittings_safety_valve', e.target.value)}
                              className="w-full px-2.5 py-1.5 border border-gray-300 rounded text-xs bg-white"
                            >
                              {CONDITION_OPTIONS.map((c) => <option key={c} value={c}>{c}</option>)}
                            </select>
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Stop Valve</label>
                            <select
                              value={fields.values.fittings_stop_valve}
                              onChange={(e) => fields.setField('fittings_stop_valve', e.target.value)}
                              className="w-full px-2.5 py-1.5 border border-gray-300 rounded text-xs bg-white"
                            >
                              {CONDITION_OPTIONS.map((c) => <option key={c} value={c}>{c}</option>)}
                            </select>
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Reducing Valve</label>
                            <select
                              value={fields.values.fittings_reducing_valve}
                              onChange={(e) => fields.setField('fittings_reducing_valve', e.target.value)}
                              className="w-full px-2.5 py-1.5 border border-gray-300 rounded text-xs bg-white"
                            >
                              {CONDITION_OPTIONS.map((c) => <option key={c} value={c}>{c}</option>)}
                            </select>
                          </div>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-2">
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              Repairs Required (Point 13a)
                            </label>
                            <input
                              type="text"
                              value={fields.values.repairs_required}
                              onChange={(e) => fields.setField('repairs_required', e.target.value)}
                              placeholder="e.g. NIL"
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              Period for Repairs (Point 13b)
                            </label>
                            <input
                              type="text"
                              value={fields.values.repairs_period}
                              onChange={(e) => fields.setField('repairs_period', e.target.value)}
                              placeholder="e.g. Not Applicable"
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              Other Conditions (Point 13c)
                            </label>
                            <input
                              type="text"
                              value={fields.values.repairs_other_condition}
                              onChange={(e) => fields.setField('repairs_other_condition', e.target.value)}
                              placeholder="e.g. NIL"
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
                            />
                          </div>
                        </div>

                        {/* Point 14 */}
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Reduced Working Pressure Pending Repairs (Point 14)
                          </label>
                          <input
                            type="text"
                            value={fields.values.reduced_working_pressure_pending_repairs}
                            onChange={(e) => fields.setField('reduced_working_pressure_pending_repairs', e.target.value)}
                            placeholder="Default: Not Required"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
                          />
                        </div>

                        {/* Point 15 */}
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Safe Working Pressure - Sub-rule 8 (Point 15)
                          </label>
                          <input
                            type="text"
                            value={fields.values.safe_working_pressure_subrule_8}
                            onChange={(e) => fields.setField('safe_working_pressure_subrule_8', e.target.value)}
                            placeholder="Default: Not Applicable"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
                          />
                        </div>

                        {/* Point 16 */}
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Other Observations (Point 16)
                          </label>
                          <input
                            type="text"
                            value={fields.values.other_observations}
                            onChange={(e) => fields.setField('other_observations', e.target.value)}
                            placeholder="Safety valve operation and setting to be checked regularly."
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
                          />
                        </div>
                      </div>
                    )}

                    {renewError && <p className="text-sm font-medium text-red-600 bg-red-50 p-3 rounded-lg border border-red-200">{renewError}</p>}

                    <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 sticky bottom-0 bg-white/95 backdrop-blur py-2">
                      <button type="button" onClick={() => setRenewOpen(false)} className="btn btn-secondary text-sm h-10 px-4 whitespace-nowrap shrink-0">Cancel</button>
                      <button type="submit" disabled={renewSaving} className="btn btn-primary text-sm h-10 px-6 whitespace-nowrap shrink-0 inline-flex items-center gap-2 justify-center font-medium">
                        {renewSaving ? (
                          <>
                            <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                            <span>Renewing…</span>
                          </>
                        ) : (
                          <>
                            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                            <span>Renew Certificate</span>
                          </>
                        )}
                      </button>
                    </div>
                  </>
                )}
              </form>
            </div>

            {/* Right Panel: Live PDF Preview */}
            <div className="lg:col-span-7 bg-slate-100 p-4 rounded-xl border border-slate-300 flex flex-col h-full shadow-inner">
              <div className="flex items-center justify-between mb-3 px-1 flex-shrink-0">
                <span className="text-xs font-bold uppercase tracking-wider text-slate-700 flex items-center gap-1.5">
                  <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-pulse" />
                  Live PDF Preview
                </span>
                <span className="text-xs text-slate-500">
                  {selectedItem.type === 'FORM_11' ? 'Rule 61 — Form 11' : 'Rule 60 — Form 10'}
                </span>
              </div>

              {certLoading ? (
                <div className="h-full flex items-center justify-center text-sm text-slate-500 flex-col gap-2">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600" />
                  Loading certificate data…
                </div>
              ) : livePdfBlobUrl ? (
                <iframe
                  src={livePdfBlobUrl}
                  className="w-full h-full min-h-[580px] rounded-lg border border-slate-300 shadow bg-white"
                  title="Live Renewed Certificate PDF Preview"
                />
              ) : (
                <div className="h-full flex items-center justify-center text-sm text-slate-500">
                  Generating PDF document preview…
                </div>
              )}
            </div>
          </div>
        )}
      </Modal>

      {/* Floating Multi-Selection Action Bar */}
      {isWrite && selectedIds.size > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 bg-slate-900/95 text-white px-5 py-3 rounded-2xl shadow-2xl backdrop-blur flex items-center gap-4 border border-slate-700/60 animate-in fade-in slide-in-from-bottom-3 duration-200">
          <div className="flex items-center gap-2">
            <span className="w-2.5 h-2.5 rounded-full bg-emerald-400 animate-pulse" />
            <span className="text-xs font-semibold">
              <strong className="text-emerald-400 font-bold">{selectedIds.size}</strong> certificate(s) selected
            </span>
          </div>

          <div className="h-4 w-px bg-slate-700" />

          <div className="flex items-center gap-2">
            {selectedItemsList.some(i => i.type === 'FORM_9') && <button className="btn btn-primary" onClick={() => setBulkForm9Ids(selectedItemsList.filter(i => i.type === 'FORM_9').map(i => i.id))}>Bulk Renew Form 9</button>}
            {selectedItemsList.some((i) => i.type === 'FORM_10') && (
              <button
                type="button"
                onClick={() => setBulkForm10Open(true)}
                className="px-3 py-1.5 bg-blue-600 hover:bg-blue-500 text-white rounded-lg text-xs font-bold transition-all shadow-sm inline-flex items-center gap-1 cursor-pointer"
              >
                <span>⚡ Bulk Renew Form 10 ({selectedItemsList.filter((i) => i.type === 'FORM_10').length})</span>
              </button>
            )}

            {selectedItemsList.some((i) => i.type === 'FORM_11') && (
              <button
                type="button"
                onClick={() => setBulkForm11Open(true)}
                className="px-3 py-1.5 bg-purple-600 hover:bg-purple-500 text-white rounded-lg text-xs font-bold transition-all shadow-sm inline-flex items-center gap-1 cursor-pointer"
              >
                <span>⚡ Bulk Renew Form 11 ({selectedItemsList.filter((i) => i.type === 'FORM_11').length})</span>
              </button>
            )}

            <button
              type="button"
              onClick={() => setSelectedIds(new Set())}
              className="px-2.5 py-1.5 text-slate-400 hover:text-white text-xs font-medium transition-colors cursor-pointer"
            >
              Clear
            </button>
          </div>
        </div>
      )}

      <Form9BulkRenewModal open={bulkForm9Ids.length > 0} ids={bulkForm9Ids} onClose={() => setBulkForm9Ids([])} onSuccess={() => { load(); }} />
      {/* Form 10 Bulk Renew Modal */}
      {bulkForm10Open && (
        <Form10BulkRenewModal
          open={bulkForm10Open}
          onClose={() => {
            setBulkForm10Open(false);
            setBulkSelectedItems(null);
            setSelectedIds(new Set());
          }}
          onSuccess={() => {
            load();
            setBulkSelectedItems(null);
            setSelectedIds(new Set());
          }}
          selectedItems={bulkSelectedItems ? bulkSelectedItems : selectedIds.size > 0 ? selectedItemsList.filter((i) => i.type === 'FORM_10') : undefined}
          allItems={items.filter((i) => i.type === 'FORM_10')}
        />
      )}

      {/* Form 11 Bulk Renew Modal */}
      {bulkForm11Open && (
        <Form11BulkRenewModal
          open={bulkForm11Open}
          onClose={() => {
            setBulkForm11Open(false);
            setBulkSelectedItems(null);
            setSelectedIds(new Set());
          }}
          onSuccess={() => {
            load();
            setBulkSelectedItems(null);
            setSelectedIds(new Set());
          }}
          selectedItems={bulkSelectedItems ? bulkSelectedItems : selectedIds.size > 0 ? selectedItemsList.filter((i) => i.type === 'FORM_11') : undefined}
          allItems={items.filter((i) => i.type === 'FORM_11')}
        />
      )}

      {/* View Certificate PDF Modal */}
      <Modal
        open={viewModalOpen}
        onClose={closeViewModal}
        title={`Certificate Document — ${viewCertItem?.certificate_number || ''}`}
        size="xl"
      >
        {viewLoading ? (
          <div className="py-20 flex flex-col items-center justify-center space-y-3">
            <div className="animate-spin rounded-full h-9 w-9 border-b-2 border-brand-600" />
            <p className="text-sm font-medium text-gray-600">Loading Certificate PDF Document...</p>
          </div>
        ) : viewPdfBlobUrl ? (
          <div className="space-y-3">
            <div className="flex items-center justify-between bg-slate-50 p-2.5 rounded-lg border border-slate-200">
              <div className="flex items-center gap-2">
                <span className="font-mono text-sm font-bold text-gray-900">{viewCertItem?.certificate_number}</span>
                <span className={`px-2 py-0.5 rounded text-[11px] font-bold ${
                  viewCertItem?.type === 'FORM_11'
                    ? 'bg-purple-100 text-purple-800 border border-purple-200'
                    : viewCertItem?.type === 'TRAINING'
                    ? 'bg-emerald-100 text-emerald-800 border border-emerald-200'
                    : 'bg-blue-100 text-blue-800 border border-blue-200'
                }`}>
                  {viewCertItem?.type === 'FORM_9' ? 'Form 9' : viewCertItem?.type === 'FORM_11' ? 'Form 11' : viewCertItem?.type === 'TRAINING' ? 'Training Cert' : 'Form 10'}
                </span>
                <span className="text-xs text-gray-600 font-medium ml-2">{viewCertItem?.company_name} — {viewCertItem?.equipment_name}</span>
              </div>
              {viewCertItem && (
                <button
                  type="button"
                  onClick={handleDownloadViewCert}
                  className="btn btn-primary text-xs py-1.5 px-3 flex items-center gap-1.5 shadow-sm cursor-pointer"
                >
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  <span>Download PDF</span>
                </button>
              )}
            </div>

            <iframe
              src={viewPdfBlobUrl}
              className="w-full h-[76vh] rounded-lg border border-slate-300 shadow-inner bg-slate-100"
              title={`Certificate PDF ${viewCertItem?.certificate_number}`}
            />
          </div>
        ) : (
          <div className="py-12 text-center text-red-600 text-sm">
            {viewError || 'Failed to load PDF document.'}
          </div>
        )}
      </Modal>

      {/* Renewal Email Configuration Modal */}
      <RenewalEmailConfigModal
        open={emailConfigOpen}
        onClose={() => setEmailConfigOpen(false)}
        onSaved={load}
      />

      {/* Group Renewal Notice Modal */}
      <SendRenewalNoticeModal
        open={sendNoticeOpen}
        onClose={() => {
          setSendNoticeOpen(false);
          setSendNoticeGroup(null);
        }}
        group={sendNoticeGroup}
        onSent={() => {
          setSendNoticeOpen(false);
          setSendNoticeGroup(null);
          load();
        }}
      />
    </div>
  );
}

interface RenewalGroupCardProps {
  group: RenewalGroup;
  isWrite: boolean;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
  onSendGroupNotice: () => void;
  onRenewGroup?: () => void;
  onOpenViewCert: (item: RenewalItem) => void;
  onOpenRenewItem: (item: RenewalItem) => void;
  selectedIds: Set<string>;
  onToggleSelectItem: (id: string) => void;
  onToggleSelectGroup: () => void;
}

function RenewalGroupCard({
  group,
  isWrite,
  isCollapsed,
  onToggleCollapse,
  onSendGroupNotice,
  onRenewGroup,
  onOpenViewCert,
  onOpenRenewItem,
  selectedIds,
  onToggleSelectItem,
  onToggleSelectGroup,
}: RenewalGroupCardProps) {
  const allGroupSelected = group.items.length > 0 && group.items.every((i) => selectedIds.has(i.id));
  const someGroupSelected = group.items.some((i) => selectedIds.has(i.id)) && !allGroupSelected;

  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-xs overflow-hidden transition-all">
      {/* Group Card Header */}
      <div className="p-3 bg-slate-50 border-b border-slate-200 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          {isWrite && (
            <input
              type="checkbox"
              checked={allGroupSelected}
              ref={(el) => {
                if (el) el.indeterminate = someGroupSelected;
              }}
              onChange={onToggleSelectGroup}
              title="Select all certificates in this inspection batch"
              className="rounded border-slate-300 text-brand-600 focus:ring-brand-500 cursor-pointer"
            />
          )}

          <button
            type="button"
            onClick={onToggleCollapse}
            className="p-1 rounded-md hover:bg-slate-200 text-slate-500 transition-colors cursor-pointer"
            title={isCollapsed ? 'Expand group items' : 'Collapse group items'}
          >
            <svg
              className={`w-4 h-4 transform transition-transform ${isCollapsed ? '' : 'rotate-90'}`}
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M9 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>

          <span className="w-8 h-8 rounded-lg bg-blue-100 text-blue-700 font-bold flex items-center justify-center text-sm shadow-xs shrink-0">
            🏢
          </span>

          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-bold text-slate-900 text-sm">{group.company_name}</span>
              {group.short_code && <ShortCodeTag code={group.short_code} />}
              <span
                className={`inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-bold ${
                  group.type === 'FORM_11'
                    ? 'bg-purple-100 text-purple-800 border border-purple-200'
                    : group.type === 'TRAINING'
                    ? 'bg-emerald-100 text-emerald-800 border border-emerald-200'
                    : 'bg-blue-100 text-blue-800 border border-blue-200'
                }`}
              >
                {group.type === 'FORM_9' ? 'Form 9' : group.type === 'FORM_11' ? 'Form 11' : group.type === 'TRAINING' ? 'Training Cert' : 'Form 10'}
              </span>
              <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-semibold bg-blue-50 text-blue-700 border border-blue-200">
                📅 Inspection Date: {toDDMMYYYY(group.examination_date)}
              </span>
              <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-medium bg-slate-100 text-slate-700 border border-slate-200">
                {group.total_count} Certificate{group.total_count === 1 ? '' : 's'}
              </span>
            </div>

            <div className="flex items-center gap-2 mt-1 flex-wrap">
              {group.overdue_count > 0 && (
                <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold bg-red-100 text-red-800">
                  {group.overdue_count} Overdue
                </span>
              )}
              {group.expiring_soon_count > 0 && (
                <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold bg-orange-100 text-orange-800">
                  {group.expiring_soon_count} Expiring Soon
                </span>
              )}
              {group.compliant_count > 0 && (
                <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold bg-green-100 text-green-800">
                  {group.compliant_count} Compliant
                </span>
              )}
              <span className="text-[11px] text-slate-400">
                Earliest due: {formatDateOnly(group.earliest_due_date)}
              </span>
            </div>
          </div>
        </div>

        {/* Group Actions */}
        <div className="flex items-center gap-2 flex-wrap">
          {isWrite && (
            <>
              {/* Notify Group */}
              <button
                type="button"
                onClick={onSendGroupNotice}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors shadow-2xs cursor-pointer ${
                  group.email_sent_count > 0
                    ? 'bg-emerald-50 text-emerald-800 border-emerald-300 hover:bg-emerald-100'
                    : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50 hover:text-slate-900'
                }`}
                title={
                  group.email_sent_count > 0
                    ? `Group notification sent ${group.email_sent_count} time(s). Click to review/send group email notification.`
                    : 'Send consolidated renewal notification email for this group'
                }
              >
                <svg
                  className={`w-3.5 h-3.5 ${group.email_sent_count > 0 ? 'text-emerald-600' : 'text-slate-500'}`}
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path
                    d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <span>{group.email_sent_count > 0 ? `Notify Group (${group.email_sent_count})` : 'Notify Group'}</span>
              </button>

              {/* Group Bulk Renew (Form 10) */}
              {(group.type === 'FORM_9' || group.type === 'FORM_10') && onRenewGroup && (
                <button
                  type="button"
                  onClick={onRenewGroup}
                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold bg-blue-600 hover:bg-blue-700 text-white transition-colors shadow-2xs cursor-pointer"
                  title="Bulk renew all Form 10 items in this group"
                >
                  <span>⚡</span>
                  <span>Renew Group ({group.type === 'FORM_9' ? 'Form 9' : 'Form 10'})</span>
                </button>
              )}

              {/* Group Bulk Renew (Form 11) */}
              {group.type === 'FORM_11' && onRenewGroup && (
                <button
                  type="button"
                  onClick={onRenewGroup}
                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold bg-purple-700 hover:bg-purple-800 text-white transition-colors shadow-2xs cursor-pointer"
                  title="Bulk renew all Form 11 items in this group"
                >
                  <span>⚡</span>
                  <span>Renew Group (Form 11)</span>
                </button>
              )}
            </>
          )}

          <button
            type="button"
            onClick={onToggleCollapse}
            className="p-1.5 rounded-lg text-xs text-slate-500 hover:bg-slate-200 transition-colors cursor-pointer"
          >
            {isCollapsed ? 'Show Items ▼' : 'Hide Items ▲'}
          </button>
        </div>
      </div>

      {/* Items Table in Group */}
      {!isCollapsed && (
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs border-collapse">
            <thead className="bg-slate-100/70 border-b border-slate-200 text-slate-600 font-semibold uppercase tracking-wider text-[11px]">
              <tr>
                {isWrite && <th className="py-2.5 px-3 w-10 text-center"></th>}
                <th className="py-2.5 px-3 min-w-[150px]">REPORT / CERT NO.</th>
                <th className="py-2.5 px-3 min-w-[200px]">Equipment / Asset</th>
                <th className="py-2.5 px-3 min-w-[110px]">Inspection Date</th>
                <th className="py-2.5 px-3 min-w-[110px]">Next Due</th>
                <th className="py-2.5 px-3 min-w-[90px]">Days</th>
                <th className="py-2.5 px-3 min-w-[100px]">Status</th>
                <th className="py-2.5 px-3 min-w-[160px] text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 bg-white">
              {group.items.map((item) => (
                <tr
                  key={item.id}
                  className={
                    item.status === 'OVERDUE'
                      ? 'bg-red-50/40 hover:bg-red-50/60'
                      : item.status === 'EXPIRING_SOON'
                      ? 'bg-orange-50/40 hover:bg-orange-50/60'
                      : 'hover:bg-slate-50/80'
                  }
                >
                  {isWrite && (
                    <td className="py-2 px-3 text-center">
                      <input
                        type="checkbox"
                        checked={selectedIds.has(item.id)}
                        onChange={() => onToggleSelectItem(item.id)}
                        className="rounded border-slate-300 text-brand-600 focus:ring-brand-500 cursor-pointer"
                      />
                    </td>
                  )}
                  <td className="py-2 px-3">
                    <button
                      type="button"
                      onClick={() => onOpenViewCert(item)}
                      className="font-mono text-xs font-bold text-brand-700 hover:text-brand-900 hover:underline cursor-pointer text-left block"
                      title="Click to view certificate PDF"
                    >
                      {item.certificate_number}
                    </button>
                    <span
                      className={`inline-block mt-0.5 px-1.5 py-0.5 rounded text-[10px] font-bold ${
                        item.type === 'FORM_11'
                          ? 'bg-purple-50 text-purple-700 border border-purple-200'
                          : item.type === 'TRAINING'
                          ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                          : 'bg-blue-50 text-blue-700 border border-blue-200'
                      }`}
                    >
                      {item.type === 'FORM_9' ? 'Form 9' : item.type === 'FORM_11' ? 'Form 11' : item.type === 'TRAINING' ? 'Training' : 'Form 10'}
                    </span>
                  </td>
                  <td className="py-2 px-3">
                    <span className="font-medium text-slate-800 block truncate max-w-[240px]" title={item.equipment_name}>
                      {item.equipment_name}
                    </span>
                    {item.internal_id_number && item.internal_id_number !== '—' && (
                      <span className="text-[10px] font-mono text-slate-500 bg-slate-100 px-1 py-0.5 rounded inline-block mt-0.5">
                        Tag: {item.internal_id_number}
                      </span>
                    )}
                  </td>
                  <td className="py-2 px-3 text-slate-600 font-mono text-[11px] whitespace-nowrap">
                    {formatDateOnly(item.examination_date)}
                  </td>
                  <td className="py-2 px-3 font-medium text-slate-900 font-mono text-[11px] whitespace-nowrap">
                    {formatDateOnly(item.next_inspection_date)}
                  </td>
                  <td className="py-2 px-3 text-[11px] whitespace-nowrap">
                    <span
                      className={
                        item.days_until_due < 0
                          ? 'text-red-600 font-bold'
                          : item.days_until_due <= 30
                          ? 'text-orange-600 font-bold'
                          : 'text-slate-500'
                      }
                    >
                      {item.days_until_due > 0 ? `${item.days_until_due}d` : `${item.days_until_due}d overdue`}
                    </span>
                  </td>
                  <td className="py-2 px-3">
                    <span className={`badge ${STATUS_BADGE[item.status]}`}>{item.status}</span>
                  </td>
                  <td className="py-2 px-3 text-right">
                    <div className="flex items-center justify-end gap-1.5">
                      {isWrite && (
                        item.type === 'TRAINING' ? (
                          <Link
                            to="/training"
                            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold bg-emerald-50 text-emerald-700 hover:bg-emerald-100 border border-emerald-200 transition-colors"
                          >
                            <span>Training</span>
                          </Link>
                        ) : (
                          <button
                            type="button"
                            onClick={() => onOpenRenewItem(item)}
                            title="Renew this individual certificate"
                            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold bg-brand-50 text-brand-700 hover:bg-brand-100 border border-brand-200/80 transition-colors cursor-pointer"
                          >
                            <svg className="w-3 h-3 text-brand-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                            <span>Renew</span>
                          </button>
                        )
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Field({ label, required, hint, children }: { label: string; required?: boolean; hint?: React.ReactNode; children: React.ReactNode }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <label className="label">{label}{required && <span className="text-red-500 ml-1">*</span>}</label>
        {hint}
      </div>
      {children}
    </div>
  );
}
function ErrorBanner({ message, onDismiss }: { message: string; onDismiss: () => void }) {
  return <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700 flex items-center justify-between"><span>{message}</span><button onClick={onDismiss} className="text-red-500">✕</button></div>;
}
